interface CurrencyFormatterOptionsType extends Intl.NumberFormatOptions {}

type CompactNotationType = Pick<CurrencyFormatterOptionsType, "notation" | "minimumFractionDigits" | "compactDisplay">;

export enum CurrencyFormatterCurrencyOption {
  USD = "USD",
}

export enum CurrencyFormatterNotationOption {
  STANDARD = "standard",
  COMPACT = "compact",
}

export enum CurrencyFormatterStyleOption {
  CURRENCY = "currency",
}

/**
 * Defaults the currency format to a "currency" style and USD. (e.g. - 1000 --> $1,000.00)
 */
export const defaultCurrencyFormatterOptions = {
  style: CurrencyFormatterStyleOption.CURRENCY,
  currency: CurrencyFormatterCurrencyOption.USD,
};

/**
 * Adds two decimal places to the currency format. (e.g. - 1000 --> $1,000.00)
 */
export const defaultCurrencyMinimumFractionDigits = { minimumFractionDigits: 2 };

/**
 * When used within the formatUSD options param, currencyCompactNotation will return a number in compact currency format.
 * (e.g. - formatUSD(1000000) // $1,000,000.00 --> formatUSD(1000000, currencyCompactNotation) // $1M)
 */
export const currencyCompactNotation: CompactNotationType = {
  notation: CurrencyFormatterNotationOption.COMPACT,
  compactDisplay: "short",
  minimumFractionDigits: 0,
};

/**
 * Formats a number into a currency format.
 *
 * @param amount - number to be formatted (e.g., 1000 or 1000000)
 * @param options - options derived from Intl.NumberFormatOptions (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat)
 * @returns Formatted number as currency
 *
 * @example
 * formatUSD(1000) // $1,000.00
 * formatUSD(1000, currencyCompactNotation) // $1K
 * formatUSD(1000000) // $1,000,000.00
 * formatUSD(1000000, currencyCompactNotation) // $1M
 */
export const formatUSD = (amount: number, options?: CurrencyFormatterOptionsType): string => {
  const standardFormattingOptions = {
    ...defaultCurrencyFormatterOptions,
    defaultCurrencyMinimumFractionDigits,
  };

  return new Intl.NumberFormat("en-US", {
    ...standardFormattingOptions,
    ...options,
  }).format(amount);
};

/**
 * A signed change in whole dollars: "+$200,000", "−$50,000" (U+2212 minus) or "$0".
 * For deductible changes that can go either way (conditional lasers).
 */
export const formatSignedUSD = (amount: number): string => {
  const wholeDollars = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  if (amount === 0) return formatUSD(0, wholeDollars);
  return `${amount > 0 ? "+" : "−"}${formatUSD(Math.abs(amount), wholeDollars)}`;
};
